import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import {
  FlexRowCenterVertical,
  FlexColCenter,
  FlexRowCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../Defaults/Text';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { round } from '../../../utilities';
import Tooltip from '../../Defaults/Tooltip/Tooltip';
import { min } from 'moment';

interface Props {
  theme: any;
  unit: string;
  count: string;
  onChange: (newValue: string, sholudUpdateCart?: boolean) => void;
  step?: number;
  marginToUnits?: number;
  marginRight?: string;
  onStock: number;
  isB2B: boolean;
  handleInputFocus?: any;
  handleInputBlur?: any;
  backgroundColor?: string;
  title?: string;
  wholePackagesOnly?: boolean;
  defaultPackageSize: number;
  noHandlingFee?: boolean;
}

function CountSetter({
  theme,
  unit,
  count,
  onChange,
  step = 1,
  marginToUnits = 0,
  marginRight,
  onStock,
  isB2B,
  handleInputFocus,
  handleInputBlur,
  backgroundColor,
  title,
  wholePackagesOnly,
  defaultPackageSize,
  noHandlingFee,
}: Props) {
  const isMks = unit === 'Mks';
  const minCount = isMks ? 0.001 : 1;

  const handleChange = e => {
    const newValue = e.target.value;
    const validated = newValue.replace(/[^0-9 \, \.]/, '');
    return onChange(validated.replace(',', '.'), false);
  };

  const handlePlusChange = () => {
    let newValue: number | string = count
      ? round(parseFloat(count) + step, 3)
      : round(minCount + step, 3);

    const remainder = round((newValue * 10000) % round(step * 10000, 1), 2);
    newValue -= remainder / 10000;

    if (isMks) {
      newValue = round(newValue, 3).toString();
    } else {
      newValue = round(newValue, 0).toString();
    }
    setTimeout(() => {
      onChange(newValue as string);
    }, 50);
  };

  const handleBlur = e => {
    const newValue = e.target.value;
    let minValue;
    minValue =
      isNaN(newValue) || newValue < minCount ? minCount.toString() : newValue;
    const minValueReminder = round((minValue * 10000) % (step * 10000), 1);
    const onStockReminder = round((onStock * 10000) % (step * 10000), 1);
    setTimeout(() => {
      if (!isMks) {
        minValue = round(minValue, 0).toString();
      }
      if (wholePackagesOnly && !noHandlingFee) {
        if (minValue % step === 0) {
          if (handleInputBlur) {
            handleInputBlur();
          }
          return onChange(minValue);
        } else if (wholePackagesOnly && minValueReminder === onStockReminder) {
          if (handleInputBlur) {
            handleInputBlur();
          }
          return onChange(minValue);
        } else {
          if (minValue < defaultPackageSize) {
            if (handleInputBlur) {
              handleInputBlur();
            }
            return onChange(defaultPackageSize.toString().replace(',', '.'));
          }

          if (handleInputBlur) {
            handleInputBlur();
          }
          const remainder = (minValue * 10000) % (step * 10000);
          minValue -= remainder / 10000;

          // Round value to nearest step multiplier
          minValue += step;
          return onChange(round(minValue, 3).toString());
        }
      }
      onChange(minValue);

      if (handleInputBlur) {
        handleInputBlur();
      }
    }, 10);
  };

  const handleMinusChange = () => {
    let newValue = round(parseFloat(count) - step, 3);

    const remainder = (newValue * 10000) % (step * 10000);

    newValue -= remainder / 10000;

    if (remainder > 0) {
      newValue += step;
    }

    let minValue: string | number = newValue < minCount ? minCount : newValue;

    // Produktu s parametrom whole_packages_only nie je mozne nastavit mnozstvo nizsie ako jedno balenie
    if (wholePackagesOnly && minValue < defaultPackageSize) {
      return onChange(defaultPackageSize.toString());
    }

    if (isMks) {
      minValue = round(minValue, 3).toString();
    } else {
      minValue = round(minValue, 0).toString();
    }

    onChange(minValue);
  };

  const handleInputOnFocus = e => {
    onChange('');
    if (handleInputFocus) {
      handleInputFocus(e);
    }
  };

  return (
    <CountWrapper marginRight={marginRight}>
      <Wrapper backgroundColor={backgroundColor} title={title}>
        <IconWrapper title="Menej" onClick={handleMinusChange}>
          <SvgIcon
            width={8}
            height={2}
            icon={IconType.minus}
            viewBox="0 0 8 2"
            alt="Menej"
            title="Menej"
            fill={theme.colors.textPrimary}
            cursor={'pointer'}
          />
        </IconWrapper>
        <Input
          backgroundColor={backgroundColor}
          name="count"
          value={count}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          onFocus={handleInputOnFocus}
          disabled={wholePackagesOnly ? false : false}
        />
        <IconWrapper title="Viac" onClick={handlePlusChange}>
          <SvgIcon
            width={8}
            height={36}
            icon={IconType.plus}
            viewBox="0 0 8 8"
            alt="Viac"
            title="Viac"
            fill={theme.colors.textPrimary}
            cursor={'pointer'}
          />
        </IconWrapper>
      </Wrapper>
      <Unit marginToUnits={marginToUnits}>{unit}</Unit>
    </CountWrapper>
  );
}

const CountWrapper = styled(FlexRowCenterVertical)<{
  marginRight?: string;
  backgroundColor?: string;
}>`
  margin-right: ${({ marginRight }) => (marginRight ? marginRight : 0)};
`;

const Wrapper = styled(FlexRowCenter)<{ backgroundColor?: string }>`
  box-shadow: ${({ theme }) => theme.boxShadow.input};
  border: ${({ theme }) => theme.borders.secondary};
  border-radius: ${rem(2)};
  width: ${rem(120)};
  height: ${rem(48)};
  padding: ${rem(16)} 0;
  margin-right: ${rem(16)};
  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : null};
`;

const Input = styled.input<{ backgroundColor?: string }>`
  display: block;
  width: 100%;
  background: ${({ theme }) => theme.color.white};
  border: 0;
  border-top: ${({ theme }) => theme.borders.secondary};
  border-bottom: ${({ theme }) => theme.borders.secondary};
  border-radius: ${rem(2)};
  padding: 0;
  text-align: center;
  box-shadow: none;
  height: ${rem(48)};
  width: ${rem(38)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 400;
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.secondary};
  outline: none;

  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : null};

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
`;

const IconWrapper = styled(FlexColCenter)<{ backgroundColor?: string }>`
  width: 100%;
  cursor: pointer;
  height: ${rem(48)};
  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : null};
`;

const Unit = styled(TextP)<{ marginToUnits: number }>`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
  color: black;
  margin-left: ${({ marginToUnits }) =>
    marginToUnits ? rem(marginToUnits) : 0};
`;

const WithThemeCountSetter: any = withTheme(CountSetter);
export { WithThemeCountSetter as CountSetter };
